<template>
    <div class="sf__filters__section" v-bind:class="{
        'sf__filters__section--hide-unavailable': section.sequential,
        'sf__filters__section--empty-state': showEmptyState()
    }">
        <div class="sf__modal-header">
            <button v-if="!drawerFlow && index > minIndex" class="sf__filters__back" @click="goToPrev()">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.5 12L15 4.5L16.05 5.55L9.6 12L16.05 18.45L15 19.5L7.5 12Z" fill="#0E131B"/>
                </svg>
                <span>Back</span>
            </button>
            <div class="sf__filters__section-title-row">
                <h2 class="sf__modal-title h1">{{ sectionTitle }}</h2>
            </div>
        </div>

        <div class="sf__modal-body">
            <div v-if="showEmptyState()" class="sf__filters__section-empty-state">
                <div class="sf__filters__section-empty-state-icon" aria-hidden="true">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_empty_configuration_info)">
                            <path d="M25.888 21.433L24.2873 29.711C24.1752 30.3092 24.3337 30.6488 24.7739 30.6488C25.0844 30.6488 25.5534 30.5256 25.8719 30.216L25.7311 30.9479C25.2717 31.5566 24.2585 32 23.3861 32C22.2608 32 21.7822 31.2575 22.0928 29.6793L23.2741 23.5777C23.3765 23.0622 23.2837 22.8757 22.8147 22.7508L22.0928 22.6083L22.224 21.938L25.888 21.433ZM24.3993 19.5188C23.9748 19.5188 23.5677 19.3334 23.2675 19.0035C22.9673 18.6735 22.7987 18.226 22.7987 17.7594C22.7987 17.2928 22.9673 16.8453 23.2675 16.5153C23.5677 16.1854 23.9748 16 24.3993 16C24.8239 16 25.231 16.1854 25.5312 16.5153C25.8314 16.8453 26 17.2928 26 17.7594C26 18.226 25.8314 18.6735 25.5312 19.0035C25.231 19.3334 24.8239 19.5188 24.3993 19.5188Z" fill="#737373"/>
                            <circle cx="24" cy="24" r="23.5" stroke="#737373"/>
                        </g>
                        <defs>
                            <clipPath id="clip0_empty_configuration_info">
                                <rect width="48" height="48" fill="white"/>
                            </clipPath>
                        </defs>
                    </svg>
                </div>
                <h3>{{ emptyStateTitle }}</h3>
                <p>{{ emptyStateDescription }}</p>
            </div>

            <template v-else>
                <template v-for="(filter) in filters" :key="filter.id">
                    <div class="sf__filter"
                    v-if="activeValues(filter).length > 1"
                    v-bind:class="{
                        'sf__filter--selected': selections[filter.slug] != null 
                    }"
                    :data-label="getLabelHandle(filter)"
                    >
                    <div class="sf__options-header">
                        <h3 v-if="!filter.description">
                            <span v-if="filters.length > 1">{{ filter.label }}</span>
                        </h3>
                        <FilterTooltip
                            v-if="filter.description"
                            :filter="filter"
                            :results="results"
                            :trigger-label="filters.length > 1 ? filter.label : null"
                        />
                        <span v-if="drawerFlow && selections[filter.slug]" class="sf__filter__selected-text">{{ selections[filter.slug] }}</span>
                    </div>
                    <div class="sf__filter__values">
                        <div v-for="(value, j) in activeValues(filter)"
                            :key="j"
                            class="sf__filter__value"
                            @click="handleValueClick(filter, value, $event)"
                            v-bind:class="{
                                'sf__filter__value--with-image': filter.has_image,
                                'sf__filter__value--active': isSelected(filter.slug, value.value),
                                'sf__filter__value--unavailable': isUnavailable(filter.slug, value.value),
                                'sf__filter__value--unavailable-tooltip-open': isUnavailableTooltipOpen(filter, value),
                            }"
                            >
                                <div v-if="filter.has_image" class="sf__filter__value__image">
                                    <img
                                        v-if="getFilterValueImage(filter, value)"
                                        :src="getFilterValueImage(filter, value)"
                                        :alt="value.value"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <span
                                        v-if="isUnavailable(filter.slug, value.value)"
                                        class="sf__filter__value__unavailable-line"
                                        aria-hidden="true"
                                    ></span>
                                    <button
                                        v-if="isDrawerFlow() && isUnavailable(filter.slug, value.value)"
                                        type="button"
                                        class="sf__filter__value__unavailable-trigger"
                                        :aria-expanded="isUnavailableTooltipOpen(filter, value).toString()"
                                        :aria-label="unavailableTooltipText(filter, value)"
                                        @click.stop.prevent="toggleUnavailableTooltip(filter, value)"
                                    >
                                        <span aria-hidden="true">i</span>
                                    </button>
                                </div>
                                <button
                                    v-if="isDrawerFlow() && !filter.has_image && isUnavailable(filter.slug, value.value)"
                                    type="button"
                                    class="sf__filter__value__unavailable-trigger"
                                    :aria-expanded="isUnavailableTooltipOpen(filter, value).toString()"
                                    :aria-label="unavailableTooltipText(filter, value)"
                                    @click.stop.prevent="toggleUnavailableTooltip(filter, value)"
                                >
                                    <span aria-hidden="true">i</span>
                                </button>
                                <div
                                    v-if="isDrawerFlow() && isUnavailableTooltipOpen(filter, value)"
                                    class="sf__filter__value__unavailable-tooltip"
                                    role="tooltip"
                                >
                                    {{ unavailableTooltipText(filter, value) }}
                                </div>
                                <p class="sf__filter__value__label" v-bind:class="{'sf__filter__value__label--oem': value.value.toLowerCase().includes('oem') }" >{{ value.value.replace('OEM', '').trim() }}</p>
                        </div>
                    </div>
                    </div>
                    <div class="sf__filter"  v-if="activeValues(filter).length == 1 && foam && alerts[filter.slug] != null">
                        <h3><span>{{ filter.label }}</span></h3>
                        <div class="sf__filter__restricted" v-for="(value, index) in activeValues(filter)" :key="index">
                            <div v-if="filter.has_image" class="sf__filter__restricted__image">
                                <img
                                    v-if="value.image"
                                    :src="value.image"
                                    :alt="value.value"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                            <div class="sf__filter__restricted__content">
                                <p class="sf__filter__restricted__label">{{ value.value }}</p>
                                <div class="sf__alert">
                                    <p v-html="alerts[filter.slug].message"></p>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </template>
        </div>

        <div class="sf__filters__section__actions sf__modal-footer" v-if="popup">
            <button v-if="drawerFlow || index > minIndex" class="sf__filters__apply" @click="goToPrev()">Back</button>
            <button
                class="sf__filters__apply sf__filters__apply--primary"
                @click.stop="handlePrimaryAction()"
                @mouseenter="openPrimaryTooltip()"
                @mouseleave="closePrimaryTooltip()"
                @focus="openPrimaryTooltip()"
                @blur="closePrimaryTooltip()"
                :aria-label="primaryButtonLabel"
                :aria-disabled="isPrimaryActionDisabled().toString()"
                :aria-describedby="isPrimaryActionDisabled() ? primaryTooltipId : null"
            >
                {{ primaryButtonLabel }}
                <span
                    v-if="primaryTooltipOpen && isPrimaryActionDisabled()"
                    :id="primaryTooltipId"
                    class="sf__filters__primary-action-tooltip"
                    role="tooltip"
                >
                    {{ primaryTooltipText }}
                </span>
            </button>
        </div>
    </div>
</template>

<script>
import FilterTooltip from './FilterTooltip.vue'; 
import {getFilterAppMessage, getSingleOptionMessage} from '../helpers/filterAppMessages';

export default {
    props: ['section', 'filters', 'selections', 'results', 'alerts', 'index', 'totalSections', 'minIndex', 'maxIndex', 'foam', 'stopped', 'popup', 'drawerFlow', 'fullConfigurationBypassed'],

    components: {
        FilterTooltip
    }, 

    data() {
        return {
            unavailableTooltipKey: null,
            primaryTooltipOpen: false
        };
    },

    mounted() {
        document.addEventListener('click', this.closeUnavailableTooltip);
        this.init();
    }, 

    beforeUnmount() {
        document.removeEventListener('click', this.closeUnavailableTooltip);
    },

    computed: {

        // This is to know the selections local to this section. 
        section_selections() {
            let selections = {}; 
            this.filters.forEach ( filter  => {
                if ( typeof this.selections[filter.slug] !== 'undefined') {
                    selections[filter.slug] = this.selections[filter.slug]; 
                }
            }); 

            return selections; 
        },

        // this is to know the count of completed selections. 
        completed() {
            return Object.values(this.section_selections).filter(v => v != null).length; 
        }, 

        available_section_count() {
            return Object.keys(this.section_selections)
                .filter(key => {
                    let filter = this.filters.find(f => f.slug == key ); 
                    let values = this.activeValues(filter); 
                    for (const k in values) {
                        if ( this.firstAvailable(filter.slug, values[k].value) ) {
                            return true; 
                        }
                    }

                    return false; 
            }); 
        },

        sectionTitle() {
            if (this.drawerFlow && this.section.title) {
                return this.section.title;
            }

            return this.filters.length == 1 ? this.filters[0].label : this.section.title;
        },

        primaryButtonLabel() {
            if (this.showEmptyState()) {
                return this.index == this.maxIndex ? 'View Products' : 'Continue to Material';
            }

            if (!this.drawerFlow) {
                return (this.index == this.maxIndex) ? 'Apply Filters' : 'Next';
            }

            if (this.index == this.maxIndex) {
                return this.section.finalLabel || 'View Products';
            }

            return this.section.nextLabel || 'Next';
        },

        primaryTooltipId() {
            return `sf-filters-primary-tooltip-${this.index}`;
        },

        primaryTooltipText() {
            return 'Complete all selections to continue';
        },

        emptyStateTitle() {
            if (this.isFullConfigurationBypassed()) {
                return getFilterAppMessage('configurationHeading');
            }

            if (this.isDrawerFlow() && this.section.step === 'material') {
                return getFilterAppMessage('materialHeading');
            }

            return getFilterAppMessage('fitmentHeading');
        },

        emptyStateDescription() {
            if (this.isFullConfigurationBypassed()) {
                return getFilterAppMessage('configurationDescription');
            }

            if (this.isDrawerFlow() && this.section.step === 'material') {
                return getFilterAppMessage('materialDescription');
            }

            return getFilterAppMessage('fitmentDescription');
        }
    },

    methods: {
        isFullConfigurationBypassed() {
            return this.fullConfigurationBypassed === true
                || this.fullConfigurationBypassed === 'true';
        },

        init() {
            this.filters.forEach( filter => {
                let values = this.activeValues(filter);

                // if for this filter there's only one value, we store the alert to show 
                // during summary. 
                this.alerts[filter.slug] = ( values.length === 1)
                    ? {
                        label: filter.label || filter.slug.replace(/_/g, ' '), 
                        message: getSingleOptionMessage(values[0].value),
                        value: values[0].value,
                        image: this.getFilterValueImage(filter, values[0]) || null, 
                    } : null;  

                if (
                    this.selections[filter.slug] != null
                    && values.map ( value => value.value ).includes( this.selections[filter.slug] )
                ) {
                    return; 
                }

                if ( values.length > 1) {
                    this.selectValue(filter.slug, null); 
                } else {
                    this.removeOption(filter.slug); 
                }
            });

            if (this.isDrawerFlow() && !this.isOneActiveValueForFilter()) {
                this.$emit('stopFilters');
                return;
            }

            // This is because in some scenarios we might end up without options for
            // the filter, so what we need to do is just to jump to the next screen.
            if (
                !this.stopped
                && (!this.isOneActiveValueForFilter() || this.isSelectionComplete())
            ) {
                this.goToNext(); 
            } else {
                this.$emit('stopFilters'); 
            }
        }, 

        /**
         * Is there at least one active value for a given filter?
         */
        isOneActiveValueForFilter() {
            return this.filters.some( filter => {
                return this.activeValues(filter).length > 1; 
            }); 
        }, 

        showEmptyState() {
            return this.isDrawerFlow() && !this.isOneActiveValueForFilter();
        },

        /**
         * Have all values been selected? 
         */
        isSelectionComplete() {
            const requiredFilters = this.filters.filter(filter => {
                const values = this.activeValues(filter);

                return values.length > 1 && values.some(value => {
                    return !this.isUnavailable(filter.slug, value.value);
                });
            });

            const hasAllAvailableSelections = requiredFilters.every(filter => {
                const value = this.selections[filter.slug];

                return value != null && !this.isUnavailable(filter.slug, value);
            });

            const availableSelections = this.filters.reduce((selections, filter) => {
                const value = this.selections[filter.slug];

                if (value != null && !this.isUnavailable(filter.slug, value)) {
                    selections[filter.slug] = value;
                }

                return selections;
            }, {});

            return hasAllAvailableSelections
                && this.isSelectionAvailable(availableSelections);
        }, 

        /**
         * Selects a value for that specific slug. 
         */
        selectValue(slug, value, force) {
            force = ( force != null ) ? force : false; 
            this.$emit('select', {slug, value, force}); 
        }, 

        handleValueClick(filter, value, event) {
            if (this.isDrawerFlow() && this.isUnavailable(filter.slug, value.value)) {
                event?.preventDefault();
                event?.stopPropagation();
                this.openUnavailableTooltip(filter, value);
                return;
            }

            this.closeUnavailableTooltip();
            this.selectValue(filter.slug, value.value, false);
        },

        /**
         * Emits an event to remove the slug from 
         * the selections list. 
         */
        removeOption(slug) {
            this.$emit('removeOption', {slug}); 
        }, 

        /**
         * Checks if the given value is selected for that slug. 
         */
        isSelected(slug, value) {
            return this.selections[slug] == value; 
        },

        isDrawerFlow() {
            return this.drawerFlow === true || this.drawerFlow === 'true';
        },

        isUnavailable(slug, value) {
            return (!this.section.sequential && !this.firstAvailableScoped(slug, value))
                || (this.section.sequential && !this.firstAvailableLinear(slug, value));
        },

        unavailableKey(filter, value) {
            return `${filter.slug}:${value.value}`;
        },

        isUnavailableTooltipOpen(filter, value) {
            return this.unavailableTooltipKey === this.unavailableKey(filter, value);
        },

        openUnavailableTooltip(filter, value) {
            this.unavailableTooltipKey = this.unavailableKey(filter, value);
        },

        toggleUnavailableTooltip(filter, value) {
            const key = this.unavailableKey(filter, value);
            this.unavailableTooltipKey = this.unavailableTooltipKey === key ? null : key;
        },

        closeUnavailableTooltip() {
            this.unavailableTooltipKey = null;
            this.closePrimaryTooltip();
        },

        isPrimaryActionDisabled() {
            return !this.showEmptyState() && !this.isSelectionComplete();
        },

        openPrimaryTooltip() {
            if (this.isPrimaryActionDisabled()) {
                this.primaryTooltipOpen = true;
            }
        },

        closePrimaryTooltip() {
            this.primaryTooltipOpen = false;
        },

        handlePrimaryAction() {
            if (this.isPrimaryActionDisabled()) {
                this.openPrimaryTooltip();
                return;
            }

            this.closePrimaryTooltip();
            this.goToNext();
        },

        unavailableTooltipText(filter, value) {
            const conflict = this.getUnavailableConflict(filter, value);

            if (conflict?.value) {
                return `Not available with the selected ${conflict.label}: ${conflict.value}`;
            }

            return 'Not available with the current selection';
        },

        getUnavailableConflict(filter, value) {
            const selection = {
                ...this.selections,
                [filter.slug]: value.value
            };
            const selectedKeys = [
                ...this.filters.map(filter => filter.slug),
                ...Object.keys(this.selections)
            ].filter((slug, index, keys) => {
                return slug !== filter.slug
                    && this.selections[slug] != null
                    && keys.indexOf(slug) === index;
            });

            const conflictSlug = selectedKeys.find(slug => {
                const relaxedSelection = {...selection};
                delete relaxedSelection[slug];
                return this.isSelectionAvailable(relaxedSelection);
            });

            if (!conflictSlug) return null;

            return {
                label: this.getFilterDisplayLabel(conflictSlug),
                value: this.getFilterDisplayValue(this.selections[conflictSlug])
            };
        },

        isSelectionAvailable(selection) {
            return this.results.some(result => {
                for (const key in selection) {
                    if (!selection[key]) continue;

                    if (!result.optionMatches(key, selection[key])) {
                        return false;
                    }
                }

                return true;
            });
        },

        getFilterDisplayLabel(slug) {
            const labels = {
                cab_configuration: 'Cab Type',
                seat_configuration_1: 'Seat Style',
                material: 'Material',
                material_configuration: 'Material',
                color: 'Color',
                insert: 'Insert',
                insert_type: 'Insert',
                pattern: 'Pattern',
                part: 'Part',
                seat_position: 'Seat Position',
                style: 'Style',
                piping: 'Piping'
            };
            const filter = this.filters.find(filter => filter.slug === slug);

            return labels[slug]
                || filter?.label?.replace(/\s+configuration$/i, '')
                || slug.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
        },

        getFilterDisplayValue(value) {
            return String(value || '').replace('OEM', '').trim();
        },

        /**
         * Checks if based on previous selections, there's at least one result 
         * available for that slug and value. 
         */
        firstAvailable(slug, value) {

            return this.results.find( result => {
                for (const key in this.selections) {

                    if ( key != slug && !this.selections[key] ) {
                        continue;
                    } 

                    let compareValue = (key == slug) ? value : this.selections[key]; 
                    
                    if ( !result.optionMatches(key, compareValue)) {
                        return false; 
                    }
                }

                return true; 
            }); 
        },

        /**
         * Non-sequential sections can include parent and child filters together.
         * A later child selection should not make an earlier parent unavailable.
         */
        firstAvailableScoped(slug, value) {
            const selections = this.scopedAvailabilitySelections(slug, value);

            return this.results.find(result => {
                for (const key in selections) {
                    if (!selections[key]) continue;

                    if (!result.optionMatches(key, selections[key])) {
                        return false;
                    }
                }

                return true;
            });
        },

        scopedAvailabilitySelections(slug, value) {
            const sectionSlugs = this.filters.map(filter => filter.slug);
            const currentIndex = this.filters.findIndex(filter => filter.slug == slug);
            const selections = {};

            Object.keys(this.selections).forEach(key => {
                if (!sectionSlugs.includes(key) && this.selections[key] != null) {
                    selections[key] = this.selections[key];
                }
            });

            this.filters.forEach((filter, index) => {
                if (index > currentIndex || this.isMaterialParentBlockedByColor(slug, filter.slug)) {
                    return;
                }

                const selected = filter.slug == slug
                    ? value
                    : this.selections[filter.slug];

                if (selected) {
                    selections[filter.slug] = selected;
                }
            });

            return selections;
        },

        isMaterialParentBlockedByColor(parentSlug, childSlug) {
            return ['material', 'material_configuration'].includes(parentSlug)
                && childSlug == 'color';
        },

        /**
         * This checks availability but only in a linear way, respecting filters order
         * from top to bottom (first to last)
         */
        firstAvailableLinear(slug, value)
        {
            const index = this.filters.findIndex( filter => filter.slug == slug ); 
            const toVerify = this.filters.filter( (filter, i) => i <= index );
            const selections = {}; 

            toVerify.forEach ( filter => {
                selections[filter.slug] = (filter.slug == slug)
                    ? value
                    : this.selections[filter.slug]
            }); 

            return this.results.find( result => {
                for (const key in selections) {
                    if ( !selections[key] ) continue; 

                    if ( !result.optionMatches(key, selections[key]) ) {
                        return false; 
                    }
                }

                return true; 
            }); 
        }, 

        activeValues(filter) {
            return filter.values.filter( value => {
                return this.results.some( result => result.optionMatches(filter.slug, value.value) ); 
            });
        },

        goToNext() {
            this.$emit('nextSection'); 
        }, 

        goToPrev() {
            this.$emit('prevSection'); 
        }, 

        /**
         * This method allows swapping images by the first result with the image. 
         * Most scenarios will just use the same, but this gives us flexibility if we need to 
         * use a different image for a specific set of options. 
         */
        getFilterValueImage(filter, value) {
            const first_available = this.firstAvailable(filter.slug, value.value); 
            const image = first_available?.getOptionImages(filter.slug)[value.value] || null; 

            if ( first_available && image && ![null, ''].includes(image) ) {
                return image; 
            }

            return value.image || null; 
        },
        /**
         * Generate a label handle for the filter
         */
        getLabelHandle(filter) {
            return filter.label.toLowerCase().replace(/\s+/g, '-');
        },
    }


}
</script>
